import {
  isLocale,
  resolveDisplay,
  type DisplayDefaults,
  type EffectiveDisplay,
  type Locale,
} from '@ly360/i18n';
import { DisplayProvider, setLocale } from '@ly360/ui';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  api,
  bindSession,
  refreshSession,
  setAccessToken,
  type Session,
  type TenantChoice,
  type UserPreferences,
} from './api';

interface AuthState {
  session: Session | null;
  ready: boolean;
  /** Resolves to a tenant chooser when the email belongs to several organisations. */
  /** `chosenLocale`: a language picked on the sign-in screen, which wins over the stored preference and is saved. */
  login: (
    email: string,
    password: string,
    tenant?: string,
    chosenLocale?: Locale,
  ) => Promise<TenantChoice | null>;
  logout: () => Promise<void>;
  can: (perm: string) => boolean;
  savePreferences: (p: Partial<UserPreferences>) => Promise<void>;
  setTenant: (t: Session['tenant']) => void;
}

const AuthContext = createContext<AuthState | null>(null);

/** Before sign-in nothing time-sensitive is shown; the device zone is only a placeholder. */
const DEVICE_DEFAULTS: DisplayDefaults = {
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  timeFormat: '12h',
  dateStyle: 'medium',
  calendar: 'gregory',
  numerals: 'latn',
  weekStart: 1,
  locale: 'en',
};

export function effectiveDisplay(s: Session | null): EffectiveDisplay {
  if (!s) return resolveDisplay(DEVICE_DEFAULTS);
  const d = s.tenant.defaults;
  const p = s.user.preferences;
  return resolveDisplay(
    { ...d, locale: isLocale(d.locale) ? d.locale : 'en' },
    {
      timezone: p.timezone,
      timeFormat: p.timeFormat,
      dateStyle: p.dateStyle,
      calendar: p.calendar,
      numerals: p.numerals,
    },
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  const { i18n } = useTranslation();

  // The server-side language preference wins once signed in.
  const adopt = useCallback(
    (s: Session | null) => {
      setSession(s);
      const l = s?.user.preferences.locale;
      if (isLocale(l) && l !== i18n.language) setLocale(l);
    },
    [i18n],
  );

  useEffect(() => {
    bindSession(adopt);
    void refreshSession().finally(() => setReady(true));
  }, [adopt]);

  useEffect(() => {
    if (!session) return;
    const t = setTimeout(() => void refreshSession(), Math.max(10_000, (session.expiresIn - 60) * 1000));
    return () => clearTimeout(t);
  }, [session]);

  const login = useCallback(
    async (email: string, password: string, tenant?: string, chosenLocale?: Locale) => {
      const r = await api<Session | TenantChoice>('/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password, tenant }),
      });
      if (r.status === 'select_tenant') return r;
      setAccessToken(r.accessToken);
      if (chosenLocale && chosenLocale !== r.user.preferences.locale) {
        adopt({ ...r, user: { ...r.user, preferences: { ...r.user.preferences, locale: chosenLocale } } });
        void api('/v1/me/preferences', {
          method: 'PATCH',
          body: JSON.stringify({ locale: chosenLocale }),
        }).catch(() => undefined);
      } else {
        adopt(r);
      }
      return null;
    },
    [adopt],
  );

  const logout = useCallback(async () => {
    await api('/v1/auth/logout', { method: 'POST' }).catch(() => undefined);
    setAccessToken(null);
    setSession(null);
  }, []);

  const savePreferences = useCallback(async (p: Partial<UserPreferences>) => {
    const r = await api<{ preferences: UserPreferences }>('/v1/me/preferences', {
      method: 'PATCH',
      body: JSON.stringify(p),
    });
    setSession((s) => (s ? { ...s, user: { ...s.user, preferences: r.preferences } } : s));
  }, []);

  const setTenant = useCallback(
    (tenant: Session['tenant']) => setSession((s) => (s ? { ...s, tenant } : s)),
    [],
  );

  const value = useMemo<AuthState>(
    () => ({
      session,
      ready,
      login,
      logout,
      can: (p) => !!session?.user.perms.includes(p),
      savePreferences,
      setTenant,
    }),
    [session, ready, login, logout, savePreferences, setTenant],
  );
  const display = useMemo(() => effectiveDisplay(session), [session]);
  return (
    <AuthContext.Provider value={value}>
      <DisplayProvider value={display}>{children}</DisplayProvider>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

export const asLocale = (l: string): Locale => (isLocale(l) ? l : 'en');
